import React, { createContext, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';

// Define the shape of the context
interface WebSocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  reportProgress: {
    status: string;
    progress: number;
    message: string;
  };
}

// Create the context with default values
const WebSocketContext = createContext<WebSocketContextType>({
  socket: null,
  isConnected: false,
  reportProgress: {
    status: 'idle',
    progress: 0,
    message: '',
  },
});

// Custom hook to use the WebSocket context
export const useWebSocket = () => useContext(WebSocketContext);

// WebSocket provider component
export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [reportProgress, setReportProgress] = useState({
    status: 'idle',
    progress: 0,
    message: '',
  });

  useEffect(() => {
    // Create socket connection
    const socketInstance = io(process.env.REACT_APP_API_URL || window.location.origin);

    // Set up event listeners
    socketInstance.on('connect', () => {
      console.log('WebSocket connected');
      setIsConnected(true);
    });

    socketInstance.on('disconnect', () => {
      console.log('WebSocket disconnected');
      setIsConnected(false);
    });

    socketInstance.on('reportProgress', (data) => {
      console.log('Report progress update:', data);
      setReportProgress(data);
    });

    // Save socket instance
    setSocket(socketInstance);

    // Clean up on unmount
    return () => {
      socketInstance.disconnect();
    };
  }, []);

  return (
    <WebSocketContext.Provider value={{ socket, isConnected, reportProgress }}>
      {children}
    </WebSocketContext.Provider>
  );
};